import React, { useEffect } from 'react';
import { Button, List, Toast, Dialog } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import { useBluetoothScaleStore } from '../../../store/bluetoothScaleStore';

interface BluetoothScaleSelectorProps {
  onWeightChange?: (weight: number, unit: string) => void;
  onConnectionChange?: (connected: boolean) => void;
}

const BluetoothScaleSelector: React.FC<BluetoothScaleSelectorProps> = ({ onWeightChange, onConnectionChange }) => {
  const { t } = useTranslation();

  // 使用全局状态
  const {
    isConnected,
    deviceName,
    connectionStatus,
    currentWeight,
    weightUnit,
    currentWeightData,
    isScanning,
    discoveredDevices,
    showDeviceList,
    initAdapter,
    setShowDeviceList,
  } = useBluetoothScaleStore();

  // 初始化适配器（只在第一次挂载时）
  useEffect(() => {
    initAdapter();
  }, [initAdapter]);

  // 通知父组件连接状态变化
  useEffect(() => {
    if (onConnectionChange) {
      onConnectionChange(isConnected);
    }
  }, [isConnected, onConnectionChange]);

  // 通知父组件重量数据变化
  useEffect(() => {
    if (onWeightChange && currentWeightData) {
      onWeightChange(currentWeightData.weight, currentWeightData.unit);
    }
  }, [currentWeightData, onWeightChange]);

  /**
   * 搜索蓝牙设备
   */
  const handleScanDevices = async () => {
    const { scanDevices, updateConnectionStatus } = useBluetoothScaleStore.getState();

    updateConnectionStatus(t('pda.pages.bluetoothScale.searching') as string);

    try {
      // 扫描10秒，每500ms实时更新设备列表
      // 发现设备会自动弹出选择框，无需等待
      await scanDevices();

      // 扫描完成后检查是否找到设备
      const { discoveredDevices } = useBluetoothScaleStore.getState();

      if (discoveredDevices.length === 0) {
        // 整个扫描期间未发现任何设备
        updateConnectionStatus(t('pda.pages.bluetoothScale.searchComplete') as string);
        Toast.show({
          icon: 'fail',
          content: t('pda.pages.bluetoothScale.noDeviceFoundToast') as string,
        });
      } else {
        // 扫描完成且已发现设备
        updateConnectionStatus(t('pda.pages.bluetoothScale.searchComplete') as string);
      }
    } catch (error) {
      updateConnectionStatus(t('pda.pages.bluetoothScale.searchFailed') as string);
      Toast.show({
        icon: 'fail',
        content: t('pda.pages.bluetoothScale.searchFailedMessage', {
          error: error instanceof Error ? error.message : t('pda.common.error')
        }) as string,
      });
    }
  };

  /**
   * 连接到指定设备
   */
  const connectToDevice = async (address?: string) => {
    const { connectDevice, updateConnectionStatus } = useBluetoothScaleStore.getState();

    updateConnectionStatus(t('pda.pages.bluetoothScale.connecting') as string);

    try {
      await connectDevice(address);

      updateConnectionStatus(t('pda.pages.bluetoothScale.connected') as string);

      Toast.show({
        icon: 'success',
        content: t('pda.pages.bluetoothScale.connectSuccess') as string,
      });
    } catch (error) {
      updateConnectionStatus(t('pda.pages.bluetoothScale.connectionFailed') as string);
      Toast.show({
        icon: 'fail',
        content: t('pda.pages.bluetoothScale.connectFailedMessage', {
          error: error instanceof Error ? error.message : t('pda.common.error')
        }) as string,
      });
    }
  };

  /**
   * 断开连接
   */
  const handleDisconnect = async () => {
    const { disconnect, updateConnectionStatus } = useBluetoothScaleStore.getState();

    await disconnect();
    updateConnectionStatus(t('pda.pages.bluetoothScale.notConnected') as string);

    Toast.show({
      icon: 'success',
      content: t('pda.pages.bluetoothScale.disconnected') as string,
    });
  };

  return (
    <div className="bg-white p-4 rounded">
      <h3 className="text-base font-semibold mb-3">{t('pda.pages.bluetoothScale.title')}</h3>

      <div className="bg-gray-50 p-3 rounded-lg mb-3">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">{t('pda.pages.bluetoothScale.deviceStatus')}:</span>
          <span className={`text-sm font-medium ${isConnected ? 'text-green-600' : 'text-gray-600'}`}>
            {isConnected 
              ? (connectionStatus || t('pda.pages.bluetoothScale.connected'))
              : (connectionStatus || t('pda.pages.bluetoothScale.notConnected'))
            }
          </span>
        </div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">{t('pda.pages.bluetoothScale.deviceName')}:</span>
          <span className="text-sm">{deviceName || t('pda.pages.bluetoothScale.notConnected')}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">{t('pda.pages.bluetoothScale.currentWeight')}:</span>
          <span className="text-lg font-bold text-blue-600">
            {currentWeight} {weightUnit}
          </span>
        </div>
      </div>

      <div className="flex gap-2">
        {!isConnected ? (
          <Button
            color="primary"
            fill="solid"
            block
            onClick={handleScanDevices}
            loading={isScanning}
            disabled={isScanning}
          >
            {isScanning ? t('pda.pages.bluetoothScale.searching') : t('pda.pages.bluetoothScale.searchDevices')}
          </Button>
        ) : (
          <Button
            color="danger"
            fill="solid"
            block
            onClick={handleDisconnect}
          >
            {t('pda.pages.bluetoothScale.disconnect')}
          </Button>
        )}
      </div>

      {/* 设备列表弹窗 */}
      <Dialog
        visible={showDeviceList}
        onClose={() => setShowDeviceList(false)}
        title={t('pda.pages.bluetoothScale.selectDevice')}
        content={
          <div className="max-h-96 overflow-y-auto">
            {discoveredDevices.filter(device => device.name && device.name !== 'Unknown Device').length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('pda.pages.bluetoothScale.noDeviceFound')}
              </div>
            ) : (
              <List>
                {discoveredDevices
                  .filter(device => device.name && device.name !== 'Unknown Device')
                  .map((device, index) => (
                    <List.Item
                      key={device.address || index}
                      onClick={() => connectToDevice(device.address)}
                      clickable
                    >
                      <div>
                        <div className="font-medium">{device.name}</div>
                        <div className="text-xs text-gray-500">{device.address}</div>
                      </div>
                    </List.Item>
                  ))}
              </List>
            )}
          </div>
        }
        actions={[
          {
            key: 'cancel',
            text: t('pda.pages.bluetoothScale.cancel'),
            onClick: () => setShowDeviceList(false),
          },
        ]}
      />
    </div>
  );
};

export default BluetoothScaleSelector;

